import React, { useState, useEffect } from 'react';
import { joinGoldChitForAdmin } from '../../api/AdminApi';
import toast from 'react-hot-toast';
import { useParams } from 'react-router-dom';

const AddGoldPlan = () => {
  const [chitTitle, setChitTitle] = useState('');
  const [goldGram, setGoldGram] = useState(10);
  const [numMonths, setNumMonths] = useState(12);
  const [goldRate, setGoldRate] = useState(10000);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [location, setLocation] = useState('Salem');
  const [chitData, setChitData] = useState([]);
  const [successMessage, setSuccessMessage] = useState('');
  const { customerEmail } = useParams(); 

  // Function to calculate end date based on start date and duration
  const calculateEndDate = (start, months) => {
    if (!start || !months || months <= 0) return '';
    
    const startDateObj = new Date(start);
    const endDateObj = new Date(startDateObj);
    endDateObj.setMonth(endDateObj.getMonth() + parseInt(months));
    
    // Format to YYYY-MM-DD for input field
    return endDateObj.toISOString().split('T')[0];
  };

  // Update end date when start date or duration changes
  useEffect(() => {
    if (startDate && numMonths > 0) {
      const calculatedEndDate = calculateEndDate(startDate, numMonths);
      setEndDate(calculatedEndDate);
    }
  }, [startDate, numMonths]);

  useEffect(() => {
    if (numMonths <= 0 || goldRate <= 0 || goldGram <= 0) {
      setChitData([]);
      return;
    }

    const calculatedData = [];
    const monthlyInstallment = Math.round((goldGram * goldRate) / numMonths);
    const monthlyGoldContribution = goldGram / numMonths;

    let totalPaid = 0;
    let totalGoldCollected = 0;

    for (let i = 1; i <= numMonths; i++) {
      totalPaid += monthlyInstallment;
      totalGoldCollected += monthlyGoldContribution;

      calculatedData.push({
        month: i,
        monthlyInstallment: monthlyInstallment,
        totalPaid: Math.round(totalPaid),
        totalGoldCollected: totalGoldCollected.toFixed(2),
      });
    }

    calculatedData.push({
      month: 'Total',
      monthlyInstallment: Math.round((goldGram * goldRate)),
      totalPaid: Math.round(goldGram * goldRate),
      totalGoldCollected: goldGram,
    });

    setChitData(calculatedData);
  }, [goldGram, numMonths, goldRate]);

  const createGoldChitPayload = () => {
    const goldPayload = {
      chitTitle: chitTitle,
      goldGram: parseFloat(goldGram),
      numMonths: parseInt(numMonths),
      goldRate: parseFloat(goldRate),
      startDate: startDate,
      endDate: endDate,
      location: location,
      monthlyInstallment: Math.round((goldGram * goldRate) / numMonths),
      monthlyGoldContribution: (goldGram / numMonths),
      chitPlanData: chitData.map(item => ({
        month: item.month,
        monthlyInstallment: item.monthlyInstallment,
        totalPaid: item.totalPaid,
        totalGoldCollected: item.totalGoldCollected,
      })),
      totalPaid: Math.round(goldGram * goldRate),
      totalGoldCollected: goldGram,
    };

    return goldPayload;
  };

  const resetForm = () => {
    setChitTitle('');
    setGoldGram(0);
    setNumMonths(0);
    setGoldRate(0);
    setStartDate('');
    setEndDate('');
    setLocation('Salem');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const goldPayload = createGoldChitPayload();

    joinGoldChitForAdmin(goldPayload, customerEmail)
      .then((res) => {
        toast.success(res.data.message);
        console.log("Final Chit Scheme Payload:", goldPayload);
        
        // Reset form values to 0 after successful submission
        resetForm();
        
        // Set success message
        setSuccessMessage('Your gold chit plan has been successfully created!');
        
        // Clear success message after 5 seconds
        setTimeout(() => {
          setSuccessMessage('');
        }, 5000);
      })
      .catch((err) => {
        toast.error("Error saving Chit:", err.message);
        alert("Failed to save chitData. Please try again.");
      });
  };

  const handleBack = () => {
    window.history.back();
  };

  // Handle start date change
  const handleStartDateChange = (date) => {
    setStartDate(date);
  };

  // Handle duration change
  const handleDurationChange = (months) => {
    setNumMonths(months);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-yellow-50 py-8 px-4 sm:px-6 lg:px-8 font-sans">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={handleBack}
            className="group flex items-center space-x-2 px-6 py-3 bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg hover:shadow-xl border border-amber-200 hover:border-amber-300 transition-all duration-300 hover:scale-105"
          >
            <svg className="w-5 h-5 text-amber-600 group-hover:text-amber-700 transition-transform group-hover:-translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
            </svg>
            <span className="font-semibold text-amber-700 group-hover:text-amber-800">Back</span>
          </button>
          
          <div className="text-center flex-1 max-w-2xl">
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4 bg-gradient-to-r from-amber-600 via-orange-600 to-yellow-600 bg-clip-text text-transparent">
              Gold Chit Planner
            </h1>
            <p className="text-lg text-gray-600 font-medium">
              Plan your golden future with our comprehensive chit scheme calculator
            </p>
          </div>
          
          <div className="w-32"></div> {/* Spacer for balance */}
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-amber-100">
          {/* Success Message */}
          {successMessage && (
            <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white p-6 text-center">
              <div className="flex items-center justify-center space-x-3">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
                <p className="text-lg font-semibold">{successMessage}</p>
              </div>
            </div>
          )}

          <div className="p-8">
            {/* Form Section */}
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8 mb-10">
                {/* Input Fields */}
                {[
                  {
                    id: 'chitTitle',
                    label: 'Chit Title',
                    type: 'text',
                    value: chitTitle,
                    onChange: setChitTitle,
                    placeholder: 'e.g., 1g Gold Chit Plan'
                  },
                  {
                    id: 'goldGram',
                    label: 'Total Gold (grams)',
                    type: 'number',
                    value: goldGram,
                    onChange: setGoldGram
                  },
                  {
                    id: 'goldRate',
                    label: 'Gold Rate (₹/gram)',
                    type: 'number',
                    value: goldRate,
                    onChange: setGoldRate
                  },
                  {
                    id: 'numMonths',
                    label: 'Duration (months)',
                    type: 'number',
                    value: numMonths,
                    onChange: handleDurationChange
                  },
                  {
                    id: 'startDate',
                    label: 'Start Date',
                    type: 'date',
                    value: startDate,
                    onChange: handleStartDateChange
                  },
                  {
                    id: 'endDate',
                    label: 'End Date',
                    type: 'date',
                    value: endDate,
                    onChange: setEndDate,
                    readOnly: true, // Make end date read-only since it's auto-calculated
                    className: "bg-gray-100 cursor-not-allowed"
                  },
                  {
                    id: 'location',
                    label: 'Location',
                    type: 'text',
                    value: location,
                    onChange: setLocation,
                    placeholder: 'e.g., Chennai, New York'
                  }
                ].map((field) => (
                  <div key={field.id} className="space-y-2">
                    <label htmlFor={field.id} className="block text-sm font-semibold text-gray-700 uppercase tracking-wide">
                      {field.label}
                    </label>
                    <div className="relative">
                      <input
                        id={field.id}
                        type={field.type}
                        value={field.value}
                        onChange={(e) => field.onChange(e.target.value)}
                        className={`w-full px-4 py-3 border border-amber-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-200 bg-amber-50/50 placeholder-gray-400 ${
                          field.readOnly ? 'bg-gray-100 cursor-not-allowed' : ''
                        }`}
                        placeholder={field.placeholder}
                        readOnly={field.readOnly}
                      />
                      <div className="absolute inset-0 rounded-xl border-2 border-transparent group-hover:border-amber-300 transition-all duration-200 pointer-events-none"></div>
                    </div>
                    {field.id === 'endDate' && endDate && (
                      <p className="text-xs text-green-600 font-medium mt-1">
                        ✅ Automatically calculated from start date and duration
                      </p>
                    )}
                  </div>
                ))}
              </div>

              {/* Submit Button */}
              <div className="flex justify-center">
                <button
                  type="submit"
                  className="group relative px-12 py-4 bg-gradient-to-r from-amber-500 to-yellow-600 text-white font-bold rounded-2xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 overflow-hidden"
                >
                  <span className="relative z-10 flex items-center space-x-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                    </svg>
                    <span>Join Gold Chit Plan</span>
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-yellow-600 to-amber-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </button>
              </div>
            </form>

            {/* Summary Cards */}
            {chitData.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12 mb-8">
                <div className="bg-gradient-to-br from-amber-100 to-yellow-100 p-6 rounded-2xl border border-amber-200">
                  <div className="text-amber-600 font-bold text-lg mb-2">Total Investment</div>
                  <div className="text-2xl font-bold text-gray-900">
                    ₹{(goldGram * goldRate).toLocaleString()}
                  </div>
                </div>
                <div className="bg-gradient-to-br from-yellow-100 to-amber-100 p-6 rounded-2xl border border-yellow-200">
                  <div className="text-amber-600 font-bold text-lg mb-2">Monthly Installment</div>
                  <div className="text-2xl font-bold text-gray-900">
                    ₹{Math.round((goldGram * goldRate) / numMonths).toLocaleString()}
                  </div>
                </div>
                <div className="bg-gradient-to-br from-amber-100 to-yellow-100 p-6 rounded-2xl border border-amber-200">
                  <div className="text-amber-600 font-bold text-lg mb-2">Gold Accumulated</div>
                  <div className="text-2xl font-bold text-gray-900">
                    {goldGram}g
                  </div>
                </div>
              </div>
            )}

            {/* Table Section */}
            {chitData.length > 0 && (
              <div className="mt-12">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-2xl font-bold text-gray-900">Payment Schedule</h3>
                  <div className="flex items-center space-x-2 text-amber-600">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd"></path>
                    </svg>
                    <span className="font-semibold">{numMonths} Months Plan</span>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-yellow-100 to-amber-100 rounded-2xl overflow-hidden shadow-lg">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-gradient-to-r  from-amber-500 to-yellow-600 text-white">
                          <th className="px-6 py-4 text-left font-bold uppercase tracking-wider text-sm">Month</th>
                          <th className="px-6 py-4 text-right font-bold uppercase tracking-wider text-sm">Gold (g)</th>
                          <th className="px-6 py-4 text-right font-bold uppercase tracking-wider text-sm">Installment</th>
                          <th className="px-6 py-4 text-right font-bold uppercase tracking-wider text-sm">Total Paid</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-amber-100/20">
                        {chitData.map((row, index) => (
                          <tr 
                            key={index} 
                            className={`transition-colors duration-200 hover:bg-amber-50/10 ${
                              row.month === 'Total' 
                                ? 'bg-yellow-100/20 font-bold text-black' 
                                : 'text-black'
                            }`}
                          >
                            <td className={`px-6 py-4 whitespace-nowrap ${row.month === 'Total' ? 'text-lg' : ''}`}>
                              {row.month}
                            </td>
                            <td className="px-6 py-4 text-right whitespace-nowrap">
                              {row.totalGoldCollected}
                            </td>
                            <td className="px-6 py-4 text-right whitespace-nowrap">
                              {row.monthlyInstallment !== null ? `₹${row.monthlyInstallment.toLocaleString()}` : '-'}
                            </td>
                            <td className="px-6 py-4 text-right whitespace-nowrap">
                              ₹{row.totalPaid.toLocaleString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer Note */}
        <div className="text-center mt-8 text-gray-600 text-sm">
          <p>💫 Plan your golden future with confidence</p>
        </div>
      </div>
    </div>
  );
};

export default AddGoldPlan;